import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Button,
  Stack,
  Paper,
  Rating,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Hotel as HotelIcon,
  Star as StarIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Reply as ReplyIcon,
  Forward as ForwardIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import feedbackService, { Feedback, FeedbackForward } from '../../services/feedbackService';

const FeedbackDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Reply dialog state
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  
  // Forward dialog state
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [forwardEmail, setForwardEmail] = useState('');
  const [forwardComments, setForwardComments] = useState('');
  const [forwardSending, setForwardSending] = useState(false);
  const [showForwardHistory, setShowForwardHistory] = useState(false);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');

  // Fetch feedback details
  useEffect(() => {
    const fetchFeedback = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await feedbackService.getFeedback(parseInt(id, 10));
        setFeedback(data);
        setReplyText(data.reply || '');
      } catch (err: any) {
        console.error('Error fetching feedback:', err);
        if (err.response && err.response.status === 401) {
          setError('Authentication error: You need to log in again to view this data.');
        } else if (err.response && err.response.status === 404) {
          setError('Feedback not found.');
        } else {
          setError(`Failed to load feedback: ${err.message || 'Unknown error'}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [id]);

  // Format date helper
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'dd MMM yyyy, hh:mm a');
    } catch (error) {
      return dateString;
    }
  };

  // Reply handlers
  const handleOpenReplyDialog = () => {
    setReplyDialogOpen(true);
  };

  const handleCloseReplyDialog = () => {
    setReplyDialogOpen(false);
  };

  const handleSendReply = async () => {
    if (!feedback || !replyText.trim()) {
      setSnackbarMessage('Please enter a reply message');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setReplySending(true);
      const updatedFeedback = await feedbackService.replyToFeedback(feedback.id, replyText);
      setFeedback(updatedFeedback);
      
      setSnackbarMessage('Reply sent successfully to customer email');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseReplyDialog();
    } catch (err: any) {
      console.error('Error sending reply:', err);
      setSnackbarMessage(err.response?.data?.message || 'Failed to send reply');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setReplySending(false);
    }
  };

  // Forward handlers
  const handleOpenForwardDialog = () => {
    setForwardDialogOpen(true);
    setForwardEmail('');
    setForwardComments('');
    setShowForwardHistory(false);
  };

  const handleCloseForwardDialog = () => {
    setForwardDialogOpen(false);
    setForwardEmail('');
    setForwardComments('');
    setShowForwardHistory(false);
  };

  const handleSendForward = async () => {
    if (!feedback || !forwardEmail.trim()) {
      setSnackbarMessage('Please enter a department email address');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forwardEmail)) {
      setSnackbarMessage('Please enter a valid email address');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setForwardSending(true);
      await feedbackService.forwardFeedback(feedback.id, forwardEmail, forwardComments);
      
      // Refresh feedback to get updated forwards
      const updatedFeedback = await feedbackService.getFeedback(feedback.id);
      setFeedback(updatedFeedback);
      
      setSnackbarMessage(`Feedback forwarded successfully to ${forwardEmail}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseForwardDialog();
    } catch (err: any) {
      console.error('Error forwarding feedback:', err);
      setSnackbarMessage(err.response?.data?.message || 'Failed to forward feedback');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setForwardSending(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !feedback) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/feedback')}
          sx={{ mb: 2 }}
        >
          Back to Feedback List
        </Button>
        <Alert severity="error">{error || 'Feedback not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/feedback')}
          >
            Back to Feedback List
          </Button>
          <Typography variant="h4" component="h1">
            Feedback Details
          </Typography>
        </Box>
        
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={feedback.reply ? <CheckCircleIcon /> : <ReplyIcon />}
            onClick={handleOpenReplyDialog}
            color={feedback.reply ? 'success' : 'primary'}
          >
            {feedback.reply ? 'View/Edit Reply' : 'Reply'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ForwardIcon />}
            onClick={handleOpenForwardDialog}
            color={feedback.forwards_count > 0 ? 'success' : 'primary'}
          >
            Forward {feedback.forwards_count > 0 && `(${feedback.forwards_count})`}
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Guest Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" />
                Guest Information
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Guest Name
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {feedback.guest_name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon fontSize="small" />
                    Email
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {feedback.guest_email}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon fontSize="small" />
                    Phone
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {feedback.guest_phone || 'Not provided'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <HotelIcon fontSize="small" />
                    Property
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {feedback.property_name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon fontSize="small" />
                    Submitted On
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDate(feedback.created_at)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Overall Rating */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon color="primary" />
                Overall Rating
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h2" color="primary" fontWeight="bold">
                  {feedback.overall_rating}
                </Typography>
                <Rating value={feedback.overall_rating} readOnly size="large" sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  out of 5 stars
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {feedback.would_recommend ? (
                    <Chip
                      icon={<ThumbUpIcon />}
                      label="Would Recommend"
                      color="success"
                      variant="outlined"
                      size="medium"
                    />
                  ) : (
                    <Chip
                      icon={<ThumbDownIcon />}
                      label="Would Not Recommend"
                      color="error"
                      variant="outlined"
                      size="medium"
                    />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Detailed Ratings */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detailed Ratings
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Cleanliness
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={feedback.cleanliness_rating} readOnly size="small" />
                      <Typography variant="body2" fontWeight="medium">
                        {feedback.cleanliness_rating}/5
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Service
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={feedback.service_rating} readOnly size="small" />
                      <Typography variant="body2" fontWeight="medium">
                        {feedback.service_rating}/5
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Food
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={feedback.food_rating} readOnly size="small" />
                      <Typography variant="body2" fontWeight="medium">
                        {feedback.food_rating}/5
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Value
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={feedback.value_rating} readOnly size="small" />
                      <Typography variant="body2" fontWeight="medium">
                        {feedback.value_rating}/5
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Comments */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Guest Comments
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  minHeight: 100,
                  whiteSpace: 'pre-wrap'
                }}
              >
                <Typography variant="body1">
                  {feedback.comments || 'No comments provided by the guest.'}
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Reply Section */}
        {feedback.reply && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" />
                  Admin Reply
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'success.50',
                    borderColor: 'success.main',
                    minHeight: 100,
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {feedback.reply}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Replied by: {feedback.replied_by_username || 'Admin'} on {formatDate(feedback.reply_sent_at)}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Forward History */}
        {feedback.forwards && feedback.forwards.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon color="primary" />
                  Forward History ({feedback.forwards.length})
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={2}>
                  {feedback.forwards.map((forward: FeedbackForward) => (
                    <Paper
                      key={forward.id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: 'info.50',
                        borderColor: 'info.main'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="medium">
                            Forwarded to: {forward.forwarded_to_email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            By: {forward.forwarded_by_username || 'Admin'} on {formatDate(forward.forwarded_at)}
                          </Typography>
                        </Box>
                      </Box>
                      {forward.comments && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Comments:
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {forward.comments}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Reply Dialog */}
      <Dialog
        open={replyDialogOpen}
        onClose={handleCloseReplyDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {feedback.reply ? 'View/Edit Reply' : 'Reply to Feedback'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Guest: {feedback.guest_name}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Property: {feedback.property_name}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Rating: {feedback.overall_rating}/5
            </Typography>
            
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Customer Feedback:
              </Typography>
              <Typography variant="body2">
                {feedback.comments || 'No comments provided'}
              </Typography>
            </Box>
            
            {feedback.reply && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  Previous Reply by {feedback.replied_by_username || 'Admin'}:
                </Typography>
                <Typography variant="body2">
                  {feedback.reply}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Sent: {formatDate(feedback.reply_sent_at)}
                </Typography>
              </Box>
            )}
            
            <TextField
              label="Your Reply"
              multiline
              rows={6}
              fullWidth
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply to the customer here..."
              sx={{ mt: 3 }}
              helperText="This reply will be sent to the customer via email"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReplyDialog} disabled={replySending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendReply}
            variant="contained"
            disabled={replySending || !replyText.trim()}
            startIcon={replySending ? <CircularProgress size={20} /> : <ReplyIcon />}
          >
            {replySending ? 'Sending...' : 'Send Reply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Forward Dialog */}
      <Dialog
        open={forwardDialogOpen}
        onClose={handleCloseForwardDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Forward Feedback to Department
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Guest: {feedback.guest_name}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Property: {feedback.property_name}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Rating: {feedback.overall_rating}/5
            </Typography>
            
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Customer Feedback:
              </Typography>
              <Typography variant="body2">
                {feedback.comments || 'No comments provided'}
              </Typography>
            </Box>
            
            {/* Show forward history if exists */}
            {feedback.forwards && feedback.forwards.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  startIcon={<HistoryIcon />}
                  onClick={() => setShowForwardHistory(!showForwardHistory)}
                >
                  {showForwardHistory ? 'Hide' : 'Show'} Forward History ({feedback.forwards.length})
                </Button>
                
                {showForwardHistory && (
                  <Box sx={{ mt: 2, maxHeight: 200, overflowY: 'auto' }}>
                    {feedback.forwards.map((forward: FeedbackForward) => (
                      <Box key={forward.id} sx={{ p: 2, mb: 1, bgcolor: 'info.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Forwarded to: <strong>{forward.forwarded_to_email}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          By: {forward.forwarded_by_username || 'Admin'} on {formatDate(forward.forwarded_at)}
                        </Typography>
                        {forward.comments && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Comments: {forward.comments}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
            
            <TextField
              label="Department Email"
              type="email"
              fullWidth
              value={forwardEmail}
              onChange={(e) => setForwardEmail(e.target.value)}
              placeholder="department@example.com"
              sx={{ mt: 3 }}
              required
              helperText="Email address of the department to forward this feedback to"
            />
            
            <TextField
              label="Comments (Optional)"
              multiline
              rows={4}
              fullWidth
              value={forwardComments}
              onChange={(e) => setForwardComments(e.target.value)}
              placeholder="Add any comments or notes for the department..."
              sx={{ mt: 2 }}
              helperText="Optional comments that will be included in the forwarded email"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForwardDialog} disabled={forwardSending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendForward}
            variant="contained"
            disabled={forwardSending || !forwardEmail.trim()}
            startIcon={forwardSending ? <CircularProgress size={20} /> : <ForwardIcon />}
          >
            {forwardSending ? 'Sending...' : 'Forward'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedbackDetail;

